import format from 'date-fns/format';
import parse from 'date-fns/parse';
import setDay from 'date-fns/setDay';

import {
  DayOfMonthEnum,
  HourlyEnum,
  ScheduleEnum,
  WeekdayEnum,
} from '../types';

// The accepted Cronman expression is non-standard [allowed values]
// seconds [0-59]
// minutes [*, 0-59]
// hours [*, 0-23]
// day of month [*, ?, 1-31]
// month [*, 0-11]
// day of week [?, MON-SUN]
// year [*]
type DayOfMonthType = (typeof DayOfMonthEnum)[keyof typeof DayOfMonthEnum];

export const generateRandomMinute = (minutes: number): number =>
  Math.floor(Math.random() * (minutes ? minutes - 1 : 59));

/**
 * Convert an HourlyEnum value to its key.
 */
function toHourlyEnumString(hour: HourlyEnum | string): string {
  if (typeof hour === 'string') {
    return hour;
  } /* HourlyEnum, number at runtime */ else {
    return HourlyEnum[hour] as string;
  }
}

/**
 * Convert a local hour range to UTC.
 */
export function toUTCRange(hour: HourlyEnum | string): keyof typeof HourlyEnum {
  try {
    const hourStr = toHourlyEnumString(hour).substring(0, 5);

    const startTime = parse(hourStr, 'HH:mm', new Date());
    let startHour = startTime.getUTCHours();

    // if minutes time offset is different than 0 add extra hour for consistency
    if (startTime.getMinutes() > 0) {
      startHour += 1;
    }

    const start = String(startHour % 24).padStart(2, '0');
    const end = String((startHour + 1) % 24).padStart(2, '0');

    return `${start}:00 - ${end}:00` as keyof typeof HourlyEnum;
  } catch {
    return 'Invalid Date' as keyof typeof HourlyEnum;
  }
}

export function toUTCDay(
  hour: HourlyEnum | string,
  weekday: string
): keyof typeof WeekdayEnum {
  try {
    const hourStr = toHourlyEnumString(hour).substring(0, 5);
    let date = parse(`${hourStr}, ${weekday}`, 'HH:mm, EEEE', new Date());
    date = setDay(date, date.getUTCDay());
    return format(date, 'EEEE') as keyof typeof WeekdayEnum; // Full name, example: Sunday
  } catch {
    return 'Invalid Date' as keyof typeof WeekdayEnum;
  }
}

export function toUTCDayOfMonth(
  hour: HourlyEnum | string,
  dayOfMonth: DayOfMonthType | string
): string {
  try {
    const hourStr = toHourlyEnumString(hour).substring(0, 2);
    const dayOfMonthNumber = Number(dayOfMonth.replace('Day', ''));
    // Create a new date with the current year, month, and the provided day and hour
    const date = parse(`${dayOfMonthNumber} ${hourStr}`, 'd H', new Date());
    const utcDayOfMonth = date.getUTCDate();
    if (
      dayOfMonth === DayOfMonthEnum.Day1 &&
      [28, 29, 30, 31].includes(utcDayOfMonth)
    ) {
      return `L`;
    }
    return `${utcDayOfMonth}`;
  } catch {
    return 'Invalid Date';
  }
}

export const getDayWithSuffix = (day: string) => {
  const updatedDay = parseInt(day, 10);
  return day === 'last day' ? day : format(new Date(2000, 0, updatedDay), 'do');
};

export default function parseSchedule({
  schedule,
  weekday,
  hour,
  dayOfMonth,
  customCronPattern,
}: {
  schedule:
    | ScheduleEnum.Hourly
    | ScheduleEnum.Daily
    | ScheduleEnum.Weekly
    | ScheduleEnum.Monthly
    | ScheduleEnum.Custom;
  hour: HourlyEnum;
  weekday: WeekdayEnum;
  dayOfMonth: DayOfMonthType;
  customCronPattern: string;
}): string {
  const currentWeekday = WeekdayEnum[toUTCDay(hour, weekday)];
  const currentHour = HourlyEnum[toUTCRange(hour)];
  const currentMinutes = hour ? new Date().getUTCMinutes() : 0;
  const currentDayOfMonth = toUTCDayOfMonth(hour, dayOfMonth);

  return {
    [ScheduleEnum.Hourly]: `0 ${generateRandomMinute(
      currentMinutes
    )} * * * ? *`,
    [ScheduleEnum.Daily]: `0 ${generateRandomMinute(
      currentMinutes
    )} ${currentHour} * * ? *`,
    [ScheduleEnum.Weekly]: `0 ${generateRandomMinute(
      currentMinutes
    )} ${currentHour} ? * ${currentWeekday} *`,
    [ScheduleEnum.Monthly]: `0 ${generateRandomMinute(
      currentMinutes
    )} ${currentHour} ${currentDayOfMonth} * ? *`,
    [ScheduleEnum.Custom]: customCronPattern,
  }[schedule];
}

export function formatHour(hour: string, minute: string): string {
  try {
    // Passing in date of today to avoid daylight saving time error.
    // Because there were no DST back in 1900! (when year = 0)
    const today = new Date();
    return format(
      new Date(
        Date.UTC(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          Number(hour),
          Number(minute)
        )
      ),
      'haaa'
    );
  } catch {
    return 'Invalid Date';
  }
}

export function humanise(cronExpression: string): string {
  const hourly = /^0 [0-9]{1,2} \* \* \* \? \*$/;
  const daily = /^0 ?([0-9]{1,2}) ?([0-9]{1,2}) \* \* \? \*$/;
  const weekly =
    /^0 ?([0-9]{1,2}) ?([0-9]{1,2}) \? \* ?(MON|TUE|WED|THU|FRI|SAT|SUN) \*$/;
  const monthly =
    /^0 ([0-9]{1,2}) ([0-9]{1,2}) (L|[1-9]|[1-2][0-9]|30|31) \* \? \*$/;

  let result = cronExpression
    .replace(hourly, 'Every hour')
    .replace(
      daily,
      (_d, minute, hour) => `Every day at ~${formatHour(hour, minute)}`
    )
    .replace(weekly, (_d, minute, hour, day) => {
      const localDate = parse(`${day},${hour},${minute},+00`, 'EEE,H,m,x', 0);
      return `Every ${format(localDate, 'EEE')} at ~${formatHour(
        hour,
        minute
      )}`;
    })
    .replace(monthly, (_d, minute, hour, dayOfMonth) => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      let day = dayOfMonth;
      if (dayOfMonth === 'L') {
        // get the last day of the current month
        day = new Date(year, month + 1, 0).getDate();
      }
      const utcDate = Date.UTC(year, month, day, hour, minute);
      const localDate = new Date(utcDate);
      const localDayOfMonth = format(localDate, 'd');
      const localTime = format(localDate, 'h:mm a');
      return `Monthly on ${getDayWithSuffix(localDayOfMonth)} ~${localTime}`;
    });
  if (result === cronExpression) {
    result = `Cron: ${cronExpression}`;
  }

  return result;
}
